import styled from '@emotion/styled';
import React from 'react';

import {
  getColorValue,
  getRadius,
  getShadow,
  getSize,
  getOffsetFocusRing,
  u,
} from '../../utilities';

import { SvgCheck } from '../icon/available-icons/check';
import { SvgClose } from '../icon/available-icons/close';
import Align from '../align/align';
import Container from '../container/container';
import Icon from '../icon/icon';
import Text from '../text/text';

type MenuPositionsProps = {
  [key: string]:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'left-start'
    | 'right-start';
};

export const menuPositions: MenuPositionsProps = {
  left: 'bottom-start',
  right: 'bottom-end',
  ['topLeft']: 'top-start',
  ['topRight']: 'top-end',
  leftSide: 'left-start',
  rightSide: 'right-start',
  rightTop: 'right-start',
  leftTop: 'left-start',
};

const MenuContainer = styled.div<MenuProps>`
  background-color: ${getColorValue('overlay')};
  display: flex;
  flex-direction: column;
  margin: 0;
  ${props => getSize('min-width', props.minWidth)};
  ${props => getSize('max-width', props.maxWidth)};
  ${props => getSize('max-height', props.maxHeight)};
  z-index: ${props => props.zIndex};
  border: 1px solid ${getColorValue('border')};
  ${getShadow('medium')};
  ${getRadius('250')};
  overflow: hidden;
`;

const SearchWrapper = styled.div`
  flex-shrink: 0;
`;

const MenuWrapper = styled.ul<MenuProps>`
  padding: ${props =>
    props.search ? `0 ${u(1.5)} ${u(1.5)} ${u(1.5)}` : u(1.5)};
  list-style: none;
  overflow: auto;
  margin: 0;
  flex: 1 1 auto;
  min-height: 0;
`;

const MenuItemWrapper = styled.li<MenuItemProps>`
  display: ${({ hidden }) => (hidden ? 'none' : 'grid')};
  grid-auto-flow: column;
  grid-template-columns: ${props => props.columns};
  ${getSize('grid-gap', 'small')};
  ${getRadius('175')};
  align-items: center;
  min-height: ${u(5)};
  padding: 0 ${u(2)};
  cursor: ${props => (props.isDisabled ? 'default' : 'pointer')};
  &:focus-visible {
    outline: 1px solid transparent;
    ${getOffsetFocusRing()};
  }
  ${props =>
    props.isHighlighted &&
    !props.isDisabled &&
    `
    background-color: ${getColorValue('backgroundHover')};
  `};
  ${props =>
    props.keyboardMove &&
    props.isHighlighted &&
    !props.isDisabled &&
    `
    outline: 1px solid transparent;
    ${getOffsetFocusRing()};
  `};
  ${props =>
    props.hasDivider &&
    `
    position: relative;
    margin-top: ${u(3)};
    &:before {
      content: '';
      border-top: 1px solid ${getColorValue('border')};
      position: absolute;
      top: ${u(-1.5)};
      left: ${u(-1.5)};
      width: calc(100% + ${u(3)});
    }
  `};

  /* Default state: show checkmark for selected items */
  .check-icon {
    display: block;
  }

  .deselect-icon {
    display: none;
  }

  /* When highlighted (mouse hover OR keyboard navigation) and allowsDeselect: swap to X */
  ${props =>
    props.showDeselectIcon && props.isHighlighted
      ? `
    .check-icon {
      display: none;
    }
    .deselect-icon {
      display: block;
    }
  `
      : ''}
`;

const Image = styled.img<Pick<MenuItemProps, 'isDisabled'>>`
  height: 100%;
  width: auto;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
`;

export const MenuItem = ({
  isDisabled,
  isHighlighted,
  isSelected,
  icon,
  hasDivider,
  children,
  menuItemRole,
  keyboardMove,
  showDeselectIcon = false,
  ...props
}: MenuItemProps & React.ComponentProps<typeof MenuItemWrapper>) => {
  const checkColumn = isSelected ? 'auto' : '';
  const iconColumn = icon ? 'auto' : '';
  const columns = `${iconColumn} 1fr ${checkColumn}`;
  const color = isDisabled ? 'disabledContent' : undefined;
  const updatedProps = menuItemRole
    ? {
        ...props,
        role: menuItemRole,
      }
    : props;

  return (
    <MenuItemWrapper
      isHighlighted={isHighlighted}
      isDisabled={isDisabled}
      keyboardMove={keyboardMove}
      columns={columns}
      hasDivider={hasDivider}
      tabIndex={isDisabled ? -1 : 0}
      data-highlighted={isHighlighted || undefined}
      showDeselectIcon={showDeselectIcon}
      {...updatedProps}
    >
      {icon &&
        (typeof icon === 'string' ? (
          <Container radius="50" width={3} height={3} overflow="hidden">
            <Align alignment="center">
              <Image src={icon} alt="" isDisabled={isDisabled} />
            </Align>
          </Container>
        ) : (
          <Icon icon={icon} color={color} />
        ))}
      <Text color={color} hasEllipsis>
        {children}
      </Text>
      {isSelected && (
        <>
          <span className="check-icon" aria-hidden="true">
            <Icon icon={<SvgCheck />} color={color} />
          </span>
          <span className="deselect-icon" aria-hidden="true">
            <Icon icon={<SvgClose />} color={color} />
          </span>
        </>
      )}
    </MenuItemWrapper>
  );
};

const Menu = ({
  position,
  zIndex,
  minWidth,
  maxWidth,
  maxHeight,
  children,
  role,
  downshiftMenuProps = () => null,
  search,
  ...props
}: MenuProps & React.ComponentProps<typeof MenuContainer>) => {
  const updatedDownshiftMenuProps = role
    ? {
        ...downshiftMenuProps(),
        role,
      }
    : { ...downshiftMenuProps() };

  return (
    <MenuContainer
      minWidth={minWidth}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      zIndex={zIndex}
      position={position}
      {...updatedDownshiftMenuProps}
      {...props}
    >
      {search && <SearchWrapper>{search}</SearchWrapper>}
      <MenuWrapper search={search}>{children}</MenuWrapper>
    </MenuContainer>
  );
};

type MenuProps = {
  isDisabled?: boolean;
  position?: string;
  zIndex?: number;
  minWidth?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  children?: React.ReactNode;
  downshiftMenuProps?: any;
  search?: React.ReactElement;
  role?: React.AriaRole;
};

type MenuItemProps = {
  isDisabled?: boolean;
  isHighlighted?: boolean;
  keyboardMove?: boolean;
  isSelected?: boolean;
  icon?: React.ReactNode | string;
  hasDivider?: boolean;
  children?: React.ReactNode;
  columns?: string;
  hidden?: boolean;
  menuItemRole?: React.AriaRole;
  showDeselectIcon?: boolean;
};

export default Menu;
